import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { TemplatePreview } from '../../components/template-editor/TemplatePreview';
import { useGetSavedTemplateByIdQuery, useGetTemplateByIdQuery } from '../../features/templates/templatesApi';

const PreviewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // Helper to normalize MongoDB IDs (handles string or {$oid: string})
    const normalizeId = (obj: any): string => {
        if (!obj) return '';
        if (typeof obj === 'string') return obj;
        if (obj.$oid) return obj.$oid;
        if (obj._id) return normalizeId(obj._id);
        return String(obj);
    };

    // 1. Attempt to fetch from saved user designs first
    const {
        data: savedDesign,
        isLoading: isSavedLoading,
        error: savedError
    } = useGetSavedTemplateByIdQuery(id || '', {
        skip: !id
    });

    // 2. Fallback to library templates if saved design is not found or missing
    // We try library if savedDesign is missing AND (it's a 404 or we finished loading)
    const isSavedNotFound = savedError && ('status' in savedError ? savedError.status === 404 : true);
    const shouldTryLibrary = !savedDesign && (isSavedNotFound || (!isSavedLoading && !savedDesign));

    const {
        data: libraryTemplate,
        isLoading: isLibraryLoading,
        error: libraryError
    } = useGetTemplateByIdQuery(id || '', {
        skip: !id || !shouldTryLibrary
    });

    const isLoading = isSavedLoading || (shouldTryLibrary && isLibraryLoading);
    const activeDesign = savedDesign || libraryTemplate;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-900 text-white">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
                <p className="text-lg font-medium">Loading Preview...</p>
            </div>
        );
    }

    if (!activeDesign) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-900 text-white p-6 text-center">
                <h2 className="text-2xl font-bold mb-4">Preview unavailable</h2>
                <p className="text-neutral-400 mb-8 max-w-md">
                    {libraryError || savedError ? `Error: ${JSON.stringify(libraryError || savedError)}` : 'Design data not found.'}
                </p>
                <button
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium"
                >
                    Go Back
                </button>
            </div>
        );
    }

    // Shaping data for TemplatePreview:
    let rawData = activeDesign.data || {};



    // 1. Robustly parse nested 'data' if it's a string
    if (typeof rawData === 'string') {
        try {
            rawData = JSON.parse(rawData);

        } catch (e) {
            console.warn('[Preview] Failed to parse design data string', e);
            rawData = {};
        }
    }

    // 2. Extra nesting protection: Sometimes backend returns { data: { data: { ... } } }
    // We check for signature properties of Wejha templates (layers/elements/dynamicLayers)
    if (rawData && rawData.data && !rawData.dynamicLayers && !rawData.layers && !rawData.elements) {

        rawData = rawData.data;
    }

    // 3. Extract dimensions with extreme precision: 
    // Priority: pages[0] > root-layer-class > root-data > default
    const firstPageDimensions = (rawData as any).pages?.[0]?.canvasDimensions;
    
    // Find the 'root' layer in the flattened dynamicLayers if present
    const rootLayer = (rawData.dynamicLayers || rawData.layers || rawData.elements || []).find((l: any) => l.id === 'root');
    const rootClassName = rootLayer?.className || '';
    
    // Parse w-[1920px] or h-[1080px] patterns if JSON is missing explicit width/height
    const extractDim = (regex: RegExp) => {
        const match = rootClassName.match(regex);
        return match ? parseInt(match[1]) : null;
    };
    
    const parsedWidth = extractDim(/w-\[?(\d+)px\]?/);
    const parsedHeight = extractDim(/h-\[?(\d+)px\]?/);

    const templateData = {
        width: firstPageDimensions?.width || parsedWidth || rawData.width || 1172,
        height: firstPageDimensions?.height || parsedHeight || rawData.height || 659,
        ...rawData,
        id: normalizeId(activeDesign._id),
        originTemplateId: (rawData as any).id,
        name: activeDesign.name,
    };



    return (
        <div className="fixed inset-0 bg-neutral-950 z-[9999] flex flex-col overflow-hidden">
            {/* Header - Flow layout ensures flex-1 area is actually available space */}
            <div className="h-16 bg-neutral-900 border-b border-white/5 flex items-center justify-between px-6 z-10 shrink-0">
                <div className="flex flex-col">
                    <h1 className="text-white font-semibold text-lg">{activeDesign.name}</h1>
                    <p className="text-white/60 text-xs">Preview Mode • Autoplay Active</p>
                </div>

                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all"
                    title="Close Preview"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* Main Preview */}
            <div className="flex-1 flex items-center justify-center bg-black">
                <TemplatePreview data={templateData} />
            </div>

            {/* Footer Controls (Optional, keeping it clean for now) */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10 opacity-0 hover:opacity-100 transition-opacity duration-300">
                <div className="px-4 py-2 bg-black/80 backdrop-blur-md border border-white/10 rounded-full text-white/80 text-sm">
                    Press ESC to Exit
                </div>
            </div>
        </div>
    );
};

export default PreviewPage;
